import {
  ActionList,
  ActionListItem,
  Divider,
  Icon,
  SectionMessage,
  Text,
} from "@certn/cascadia-react";
import { formatMonitoringDate } from "@certn/monitoring";
import { css } from "@emotion/css";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useApplicantMonitoringClients } from "./useMonitoringApi";

export function MonitoringClientsSection() {
  const { t } = useTranslation();
  const { data: clients, isLoading, error } = useApplicantMonitoringClients();

  if (isLoading) {
    return null;
  }

  if (error) {
    return (
      <section aria-label={t("Monitoring")}>
        <Text as="h2" style="global/heading-large" className={styles.heading}>
          {t("Monitoring")}
        </Text>
        <SectionMessage
          type="danger"
          testID="monitoring-clients-error"
          sectionTitle={t("Unable to load monitoring")}
        >
          {t("Please try again later.")}
        </SectionMessage>
      </section>
    );
  }

  if (!clients?.length) {
    return null;
  }

  return (
    <section aria-label={t("Monitoring")}>
      <Text as="h2" style="global/heading-large" className={styles.heading}>
        {t("Monitoring")}
      </Text>
      <ActionList>
        {clients.map((client) => {
          const statusDate =
            client.monitor_status_rollup === "PAUSED" &&
            client.monitoring_paused_on
              ? client.monitoring_paused_on
              : client.monitoring_started_on;
          const supportingLine =
            client.monitor_status_rollup === "PAUSED" &&
            client.monitoring_paused_on
              ? t("Paused monitoring on {{date}}", {
                  date: formatMonitoringDate(statusDate),
                })
              : t("Started monitoring on {{date}}", {
                  date: formatMonitoringDate(statusDate),
                });

          return (
            <Fragment key={client.customer_account_id}>
              <Link
                to={`/monitoring/${client.customer_account_id}`}
                className={styles.link}
              >
                <ActionListItem
                  title={client.company_name}
                  trailing={
                    <Icon
                      name="chevron_right"
                      color="text-weak"
                      size="space-5"
                    />
                  }
                  centerAlignTrailing
                  testID={`monitoring-client-${client.customer_account_id}`}
                >
                  <Text style="form/body-secondary" color="text-weak">
                    {supportingLine}
                  </Text>
                  <Text style="form/body-secondary" color="text-weak">
                    {t("{{count}} checks monitored", {
                      count: client.checks_monitored,
                    })}
                  </Text>
                </ActionListItem>
              </Link>
              <Divider />
            </Fragment>
          );
        })}
      </ActionList>
    </section>
  );
}

const styles = {
  heading: css({
    marginBottom: "var(--space-4)",
  }),
  link: css({
    color: "inherit",
    textDecoration: "none",
    display: "block",
    "&:hover": {
      backgroundColor: "var(--background-ghost-hover)",
    },
  }),
};
